export const CHECKOUT_URL = "https://bcm-en.impultienda.ar/checkout/";

export const images = {
  mainMockup: "https://cdn.magicpatterns.com/uploads/js1WM1Zjr4ng5hNCx3fe6V/BCM-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/1oy4WFHsp34inR3tAnW4va/JASON_MITCHELL_-_ECU_DECISION.png",
};

export const diagnosticQuestions = [
  "Is the problem related to the BCM itself?",
  "Could it involve a power supply or a ground connection?",
  "Is it a wiring issue or an input signal?",
  "Is it a communication problem or another vehicle system?",
];

export const beforeItems = [
  "You retrieve several fault codes but aren't sure how to organize the investigation.",
  "You jump between wiring diagrams, scan tool results, technical notes, and online resources.",
  "You suspect the BCM but need a better way to evaluate other possible causes.",
  "You repeat checks because your previous findings weren't documented consistently.",
  "You struggle to distinguish a confirmed fault from a plausible explanation.",
  "Every complicated electrical problem seems to require starting from scratch.",
];

export const afterItems = [
  "You follow a structured process for organizing a new BCM-related case.",
  "You classify symptoms and record the information that matters.",
  "You separate observations, measurements, and hypotheses.",
  "You use visual decision frameworks to plan your next investigative step.",
  "You document what you've checked and what the results actually establish.",
  "You finish with a clearer record of the investigation and the remaining questions.",
];

export const pains = [
  {
    title: "You Have Multiple BCM-Related Fault Codes, but No Clear Starting Point.",
    body: "You scan the vehicle and discover several codes involving body electronics, lighting, central locking, windows, or other functions. You know there's a problem. What you need is a structured way to classify the symptoms and organize the investigation.",
  },
  {
    title: "You Spend Too Much Time Switching Between Different Technical Resources.",
    body: "Your scan tool results, wiring diagrams, notes, and previous findings are scattered across different places. Finding and organizing the information becomes part of the problem.",
  },
  {
    title: "You're Not Always Sure Whether the BCM Is Actually the Problem.",
    body: "A malfunctioning feature doesn't automatically mean the BCM is defective. You need a structured way to record what you've established, what remains uncertain, and what other possibilities require investigation.",
  },
  {
    title: "You Repeat Checks Because Your Previous Findings Weren't Recorded Properly.",
    body: "Without consistent documentation, it's difficult to remember what you tested, under which conditions, and what the results established.",
  },
  {
    title: "Intermittent Body Electronics Problems Make Your Investigation Even Harder.",
    body: "A door lock works sometimes. A lighting function behaves inconsistently. A fault appears under certain conditions and disappears under others. You need a way to record the circumstances, observations, and test results associated with each occurrence.",
  },
  {
    title: "You Know Your Diagnostic Skills Could Be More Efficient, but Your Process Isn't Consistent.",
    body: "You have experience. You have diagnostic equipment. You know how to access technical information. What you want is a repeatable framework for putting everything together.",
  },
];

export const volumes = [
  {
    number: "I",
    color: "#1E63F0",
    title: "Build Your BCM Investigation from the First Symptom",
    intro: "Before you start testing, you need to understand what you're investigating. In this volume, I'll show you how to organize the initial information so you have a clearer starting point for your case.",
    items: [
      "The BCM Case Intake Framework.",
      "A symptom and body electronics fault classification worksheet.",
      "A vehicle and system identification record.",
      "A diagnostic information collection checklist.",
      "A case prioritization matrix.",
      "A framework for separating customer complaints from verified observations.",
      "An initial investigation planning sheet.",
    ],
    result: "A more organized starting point for your next BCM-related investigation.",
  },
  {
    number: "II",
    color: "#15803D",
    title: "Map Your Investigation Before You Start Changing Anything",
    intro: "Once you've collected the initial information, the next challenge is deciding how to organize the investigation. This volume helps you turn a complicated case into a visual sequence of questions, evidence requirements, and decisions.",
    items: [
      "The BCM Investigation Blueprint.",
      "A visual case-mapping framework.",
      "Diagnostic decision-tree templates.",
      "A symptom-to-investigation planning matrix.",
      "A hypothesis tracking worksheet.",
      "A diagnostic next-step planner.",
      "A decision record explaining why a particular check was selected.",
    ],
    result: "A structured way to plan your investigation and keep track of your reasoning.",
  },
  {
    number: "III",
    color: "#EA580C",
    title: "Separate Confirmed Evidence from Possible BCM Faults",
    intro: "A fault code, a customer complaint, a measurement, and a confirmed root cause are not the same thing. This volume helps you organize the evidence so you can distinguish what you've established from what still needs to be investigated.",
    items: [
      "The BCM Evidence Tracker.",
      "A measurement and observation recording system.",
      "A hypothesis-versus-evidence matrix.",
      "A test result documentation sheet.",
      "An expected-versus-observed results framework.",
      "A checklist for identifying missing information.",
      "A decision record for each investigative step.",
    ],
    result: "A clearer record of what your investigation has established—and what remains uncertain.",
  },
  {
    number: "IV",
    color: "#7C3AED",
    title: "Organize Different Types of BCM-Related Investigations",
    intro: "Not every body electronics fault follows the same pattern. Problems involving lighting, central locking, power windows, wipers, switches, or communication with other modules can require different investigative approaches.",
    items: [
      "A BCM communication-fault investigation worksheet.",
      "A multiple-symptom classification framework.",
      "An intermittent-fault recording template.",
      "A no-communication case organizer.",
      "A power supply and ground investigation planning sheet.",
      "A body electronics symptom-to-investigation map.",
      "A framework for identifying the next question to investigate using the available evidence and applicable service information.",
    ],
    result: "A more consistent method for organizing different types of BCM-related investigations.",
    note: "These are diagnostic organization tools, not universal repair procedures. Actual electrical tests, module access procedures, wiring information, and specifications must be confirmed against the applicable manufacturer's service information.",
  },
  {
    number: "V",
    color: "#DC2626",
    title: "Turn Diagnostic Findings into Clear Next-Step Decisions",
    intro: "An investigation becomes more useful when each result helps you determine what information is needed next. This volume organizes the relationship between your test results, working hypotheses, and subsequent investigative steps.",
    items: [
      "The BCM Diagnostic Decision Matrix.",
      "A confirmed-versus-unconfirmed findings checklist.",
      "A next-step selection worksheet.",
      "A diagnostic dead-end review framework.",
      "A repeated-test tracking sheet.",
      "A verification planning checklist.",
      "A decision closure record.",
    ],
    result: "A repeatable framework for documenting why you continued, changed direction, or closed a line of investigation.",
  },
  {
    number: "VI",
    color: "#CA8A04",
    title: "Document the Case and Verify the Outcome",
    intro: "The job isn't finished simply because a symptom disappears or a fault code no longer appears. This final volume helps you turn the investigation into a clear, reviewable professional record.",
    items: [
      "The BCM Diagnostic Case Report.",
      "A pre-repair findings summary.",
      "A repair and intervention record.",
      "A post-repair verification checklist.",
      "A remaining-fault documentation sheet.",
      "A case closure worksheet.",
      "A reusable template for future investigations.",
      "A framework for building a personal reference library from completed cases.",
    ],
    result: "A clearer record of the investigation, the evidence collected, the work performed, and the verification completed.",
  },
];

export const toolkit = [
  { title: "Visual Diagnostic Workflows", body: "Map your investigation from the initial symptom through evidence collection, decision-making, and verification." },
  { title: "Decision Trees and Investigation Maps", body: "Organize the questions and potential next steps that arise during a case." },
  { title: "Evidence and Measurement Trackers", body: "Record observations, measurements, conditions, and test results consistently." },
  { title: "BCM Diagnostic Case Templates", body: "Keep the information from each investigation organized in one place." },
  { title: "Professional Checklists", body: "Use repeatable prompts to help avoid overlooking important documentation and verification steps." },
  { title: "Hypothesis Tracking Worksheets", body: "Keep possible explanations separate from verified findings so you can review the evidence more clearly." },
];

export const bonuses = [
  {
    number: 1,
    title: "BCM Quick Reference Cards™",
    value: "$37",
    image: "https://cdn.magicpatterns.com/uploads/2M9aVETMUUowrY7JGUAAqR/BONO_1_BCM-EN.png",
    intro: "A collection of concise reference cards designed to help you keep your investigation organized.",
    label: "You'll receive:",
    items: ["Case intake reminders.", "Symptom classification prompts.", "Evidence recording reminders.", "Hypothesis tracking questions.", "Documentation checkpoints.", "Case closure and verification prompts."],
    outro: "Use these cards as quick reminders when starting or reviewing a BCM-related investigation.",
  },
  {
    number: 2,
    title: "The 10-Minute BCM Case Organization Protocol™",
    value: "$27",
    image: "https://cdn.magicpatterns.com/uploads/gCDsXudGTYLKKGcPh4NZhi/BONO_2_BCM-EN.png",
    intro: "A focused workflow designed to help you organize the initial investigation in approximately 10 minutes.",
    label: "The protocol guides you through:",
    items: ["Recording the reported symptom.", "Identifying the available diagnostic information.", "Listing the known facts.", "Separating observations from assumptions.", "Identifying missing information.", "Recording the questions that remain unanswered.", "Selecting a logical next investigative step."],
    outro: "The objective is to organize the beginning of the investigation—not to promise a complete diagnosis or repair in 10 minutes.",
  },
  {
    number: 3,
    title: "BCM Troubleshooting Case Review Library™",
    value: "$37",
    image: "https://cdn.magicpatterns.com/uploads/vytpeX57uyDbni5TNu5KGz/BONO_3_BCM-EN.png",
    intro: "A collection of structured review worksheets for examining common categories of BCM-related diagnostic cases.",
    label: "Includes worksheets for:",
    items: ["Multiple body electronics fault codes.", "Modules that do not communicate.", "Intermittent body electronics symptoms.", "Problems reported after a repair.", "Electrical functions that behave inconsistently.", "Cases with incomplete or conflicting evidence."],
    outro: "Each worksheet helps you document the symptoms, available evidence, unanswered questions, and next investigative steps.",
  },
  {
    number: 4,
    title: "Professional BCM Diagnostic Case Report Kit™",
    value: "$27",
    image: "https://cdn.magicpatterns.com/uploads/vaVy9KPYfZAqUiY7PVXxZ9/BONO_4_BCM-EN.png",
    intro: "A documentation kit designed to help you present your findings in a clear and consistent format.",
    label: "You'll receive:",
    items: ["Initial case reports.", "Diagnostic findings summaries.", "Measurement records.", "Repair documentation forms.", "Verification reports.", "Remaining-fault records.", "Case closure summaries."],
    outro: "Improve the consistency of your records and make completed investigations easier to review.",
  },
  {
    number: 5,
    title: "30 BCM Investigation Checklists™",
    value: "$29",
    image: "https://cdn.magicpatterns.com/uploads/jKRKmEdyKnA78jYrFD2tYb/BONO_5_BCM-EN.png",
    intro: "A collection of checklist templates organized around different stages of a diagnostic investigation.",
    label: "Covers:",
    items: ["Initial information collection and symptom classification.", "Diagnostic information gathering.", "Evidence recording and test planning.", "Hypothesis review.", "Measurement documentation.", "Repair records.", "Post-repair verification.", "Final case documentation."],
    outro: "These checklists help you work with greater consistency without forcing every vehicle or fault into the same procedure.",
  },
];

export const offerItems = [
  { title: "BCM Diagnostic Blueprint™ — Six-Volume Collection", body: "Your complete collection of visual workflows, investigation frameworks, evidence trackers, decision tools, and case documentation templates.", value: "$97" },
  { title: "BCM Quick Reference Cards™", body: "Concise reference cards to help you organize your diagnostic process.", value: "$37" },
  { title: "The 10-Minute BCM Case Organization Protocol™", body: "A focused workflow for structuring the beginning of a BCM-related investigation.", value: "$27" },
  { title: "BCM Troubleshooting Case Review Library™", body: "Structured worksheets for reviewing common categories of BCM-related cases.", value: "$37" },
  { title: "Professional BCM Diagnostic Case Report Kit™", body: "Templates for documenting findings, measurements, repairs, and verification.", value: "$27" },
  { title: "30 BCM Investigation Checklists™", body: "A practical collection of checklists for different stages of the investigation.", value: "$29" },
];

export const offerPerks = [
  "Instant digital access.",
  "Lifetime access to your purchased materials.",
  "No monthly subscriptions.",
  "Six professional volumes.",
  "Five exclusive bonuses.",
  "Seven-day money-back guarantee.",
];

export const guaranteeChecks = [
  "Explore all six volumes.",
  "Review the diagnostic workflows.",
  "Examine the decision trees.",
  "Use the case documentation templates.",
  "Review the checklists and reference cards.",
  "Decide whether the system adds practical value to your workflow.",
];

export const useCases = [
  { title: "A More Consistent Case Intake", body: "Start each investigation with a structured record of the vehicle, symptoms, available information, and unanswered questions." },
  { title: "Clearer Evidence Documentation", body: "Keep observations, measurements, and working hypotheses separate." },
  { title: "More Organized Investigation Planning", body: "Record why a particular question or diagnostic check was selected and what information is needed next." },
  { title: "Better Case Records", body: "Maintain a clearer record of what was checked, what was established, and what remains unresolved." },
];

export const testimonials = [
  {
    name: "Mark Johnson",
    role: "Shop Owner · Ohio, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/aa5afd35-7c91-4ff3-91ae-5e324f9e4735.jpg",
    quote: "The case intake sheet alone changed how my techs start body electronics jobs. Everyone records the same things now, so when a car comes back nobody is guessing what was already checked.",
  },
  {
    name: "Darnell Brooks",
    role: "Automotive Electronics Tech · Georgia, USA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/cd2e5716-c3eb-4d54-8c3b-ce30cb1f437e.jpg",
    quote: "I used to jump straight to the module. The hypothesis-versus-evidence matrix forces me to write down what I've actually confirmed. It's made my next step a lot more deliberate.",
  },
  {
    name: "Laura Mendes",
    role: "Diagnostic Technician · Ontario, Canada",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/7a1f257e-f4ba-4646-bc11-a6d9bcc0f0f6.jpg",
    quote: "Intermittent lock and lighting faults were my least favorite jobs. The intermittent-fault template keeps every occurrence and condition in one place, and my reports look far more professional.",
  },
];

export const faqs = [
  { q: "How Will I Receive the Product?", a: "After your payment is confirmed, you'll receive instructions to access and download the digital materials. The product is delivered digitally. No physical item will be shipped." },
  { q: "Is This Suitable for Beginners?", a: "The Blueprint can help technicians who are learning to organize their diagnostic process, as well as experienced professionals who want a more consistent way to document investigations. However, it does not replace foundational automotive electrical knowledge, appropriate training, or the technical information required to work safely on a specific vehicle." },
  { q: "Does This Work with Every Vehicle?", a: "The organizational frameworks can be adapted to different vehicles, but BCM architecture, wiring, communication networks, diagnostic procedures, and specifications vary between manufacturers and models. You must consult the applicable service information for the vehicle you're working on. The Blueprint is a framework for organizing investigations, not a universal vehicle-specific repair manual." },
  { q: "Will This Tell Me Exactly Which Component Is Faulty?", a: "No. The Blueprint does not automatically identify a defective BCM or guarantee a particular diagnostic outcome. It helps you organize symptoms, record evidence, track hypotheses, and plan the next investigative step. The actual diagnosis depends on the vehicle, the fault, the tests performed, and the evidence obtained." },
  { q: "Do I Need Specialized Software?", a: "You do not need additional software to use the digital templates and worksheets. However, performing actual vehicle diagnostics may require appropriate diagnostic equipment, technical information, and tools depending on the vehicle and the investigation." },
  { q: "Are the Files Downloadable?", a: "Yes. The product is delivered digitally, and the materials are intended to be accessible as downloadable files. You'll receive the delivery instructions after your payment is confirmed." },
  { q: "Is This a Subscription?", a: "No. The purchase is a one-time payment of $27. There are no recurring monthly fees for the purchased collection." },
  { q: "Will I Receive Future Updates?", a: "The purchase includes the materials available at the time of purchase. Any future updates or additional products will be subject to the terms communicated at the time of release." },
  { q: "What If the Product Isn't Right for Me?", a: "You can review the materials during the seven-day guarantee period. If you decide the product isn't suitable for your needs, contact us to request a refund under the applicable guarantee terms." },
  { q: "Does This Replace Manufacturer Service Information?", a: "Absolutely not. The Blueprint helps you organize the diagnostic process. Manufacturer-specific service information remains essential for applicable wiring diagrams, specifications, test procedures, module configurations, and safety requirements." },
];

export const recentBuyers = [
  { name: "Carlos M.", country: "Mexico" },
  { name: "Lucía R.", country: "Argentina" },
  { name: "José L.", country: "Colombia" },
  { name: "Andrés P.", country: "Chile" },
  { name: "María F.", country: "Spain" },
  { name: "Diego S.", country: "Peru" },
  { name: "Javier T.", country: "Ecuador" },
  { name: "Sofía V.", country: "Uruguay" },
  { name: "Luis H.", country: "Dominican Republic" },
  { name: "Miguel Á.", country: "Costa Rica" },
  { name: "Ricardo G.", country: "Guatemala" },
  { name: "Fernando B.", country: "Venezuela" },
  { name: "Pablo C.", country: "Paraguay" },
  { name: "Valentina O.", country: "Bolivia" },
  { name: "Raúl N.", country: "Panama" },
];
